import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import ProductCard from '@/components/ProductCard';
import { isOutOfStock } from '@/lib/inventoryUtils';

interface Product {
  id: string;
  name: string;
  description: string;
  slug: string;
  price_per_unit: number;
  original_price?: number;
  measurement_unit: string;
  total_inventory: number;
  min_quantity: number;
  max_quantity?: number;
  step_quantity: number;
  city: string;
  sale_type: string;
  sale_start?: string;
  sale_end?: string;
  images: string[];
  package_coverage_per_unit?: number;
  minimum_packages?: number;
  categories: {
    name: string;
  };
  subcategories: {
    name: string;
  };
}

const FeaturedListings = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        // Use secure public function to exclude supplier data
        const { data: allProducts, error: fetchError } = await supabase
          .rpc('get_public_product_catalog', {
            limit_count: 100,
            offset_count: 0
          });

        if (fetchError) {
          console.error('Error fetching products:', fetchError);
          return;
        }

        // Filter out Group Buy and Liquidation products, and exclude Out of Stock
        const regularProducts = (allProducts || []).filter((p: any) =>
          !p.group_buy_enabled && !p.liquidation_sale_enabled && !isOutOfStock(p)
        );

        const shuffled = regularProducts.sort(() => 0.5 - Math.random());
        const selectedProducts = shuffled.slice(0, 4);

        // Fetch categories and subcategories data
        const categoryIds = selectedProducts.map(p => p.category_id).filter(Boolean);
        const subcategoryIds = selectedProducts.map(p => p.subcategory_id).filter(Boolean);

        const [categoriesResult, subcategoriesResult] = await Promise.all([
          categoryIds.length > 0
            ? supabase.from('categories').select('id, name').in('id', categoryIds)
            : { data: [] },
          subcategoryIds.length > 0
            ? supabase.from('subcategories').select('id, name').in('id', subcategoryIds)
            : { data: [] }
        ]);

        const categoriesMap = new Map((categoriesResult.data || []).map(c => [c.id, c]));
        const subcategoriesMap = new Map((subcategoriesResult.data || []).map(s => [s.id, s]));

        // Transform to match Product interface with proper category data
        const transformedProducts = selectedProducts.map(product => ({
          ...product,
          categories: product.category_id ? categoriesMap.get(product.category_id) || { name: 'ללא קטגוריה' } : { name: 'ללא קטגוריה' },
          subcategories: product.subcategory_id ? subcategoriesMap.get(product.subcategory_id) || { name: 'ללא תת-קטגוריה' } : { name: 'ללא תת-קטגוריה' }
        }));

        setProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);


  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-b from-muted/30 via-blue-50/30 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">מוצרים נבחרים</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-muted/30 via-blue-50/30 to-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 hebrew-heading">מוצרים במכירה רגילה</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} viewMode="grid" showRegularSaleBadge={true} />
          ))}
        </div>


      </div>
    </section>
  );
};

export default FeaturedListings;